import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { orpcClient } from './client'
import { contestListOptions } from './generated/@tanstack/react-query.gen'
import type { ContestListResponse } from './generated/types.gen'

export type ContestItem = ContestListResponse['data'][number]

/**
 * Convert the v2 paginated response to GraphQL Connection format for
 * compatibility with BaseCursorPaging and getNodesFromConnection.
 */
function toConnection(response: ContestListResponse) {
  return {
    edges: response.data.map(item => ({ node: item, cursor: item.id })),
    pageInfo: {
      hasNextPage: response.pageInfo.hasNextPage,
      hasPreviousPage: response.pageInfo.hasPreviousPage ?? false,
      startCursor: response.pageInfo.startCursor,
      endCursor: response.pageInfo.endCursor,
    },
  }
}

type ListContestsVariables = {
  before?: string
  after?: string
  first?: number
  last?: number
  published?: boolean
  active?: boolean
}

// The server uses z.stringbool() so bool filters cross the wire as strings.
// Keep the consumer API boolean-typed and serialize here.
function toQuery({ published, active, ...rest }: ListContestsVariables) {
  return {
    ...rest,
    published: published == null ? undefined : String(published),
    active: active == null ? undefined : String(active),
  }
}

// TanStack Query's structural sharing keeps `data` referentially stable when
// upstream contents haven't changed. useMemo then only recomputes the
// Connection adapter when that stable input actually changes.
function useContestConnection(raw: ContestListResponse | undefined) {
  return useMemo(() => (raw ? toConnection(raw) : undefined), [raw])
}

// Admins see unpublished contests, so cached results must not leak across
// login/logout. `dependsOn: ['login']` lets QueryCacheInvalidator reset
// these entries when auth state flips.
const CONTEST_LIST_META = { dependsOn: ['login'] }

export function useListContests(variables: ListContestsVariables = {}) {
  const { data, isFetching, error, refetch } = useQuery({
    ...contestListOptions({ client: orpcClient, query: toQuery(variables) }),
    meta: CONTEST_LIST_META,
  })
  return {
    data: useContestConnection(data),
    loading: isFetching,
    error,
    refetch,
  }
}

export function usePagedListContests(variables: ListContestsVariables = {}) {
  const { data, isFetching, error, refetch } = useQuery({
    ...contestListOptions({ client: orpcClient, query: toQuery(variables) }),
    placeholderData: keepPreviousData,
    meta: CONTEST_LIST_META,
  })
  return {
    data: useContestConnection(data),
    loading: isFetching,
    error,
    refetch,
  }
}
